import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faPlus, faTrash, faPaperPlane, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import BTCConverter from '../BTCConverter';
import { SendBitcoinModal } from './SendBitcoinModal';
import { parseApiError } from '../../lib/apiErrors';
import '../../styles/components/Styles.css';

const WalletList = () => {
  const [wallets, setWallets] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [totalBalance, setTotalBalance] = useState(0);
  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedWalletAddress, setSelectedWalletAddress] = useState('');
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const newTotalBalance = wallets.reduce((total, wallet) => total + Number(wallet.confirmedBalance ?? wallet.balance ?? 0), 0);
    setTotalBalance(newTotalBalance);
  }, [wallets]);

  const getToken = useCallback(() => getAccessTokenSilently({
    authorizationParams: {
      audience: process.env.REACT_APP_API_AUDIENCE,
    },
  }), [getAccessTokenSilently]);

  const fetchWallets = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/UserWallets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw await parseApiError(response, `Failed to fetch wallets (${response.status})`);
      }

      const data = await response.json();
      const updatedWallets = (Array.isArray(data) ? data : []).map((wallet) => ({
        ...wallet,
        collapsed: true,
      }));
      setWallets(updatedWallets);
    } catch (fetchError) {
      console.error('Error fetching wallets:', fetchError);
      setError(fetchError.message || 'Could not load BTC wallets.');
      setShowErrorModal(true);
    }
  }, [apiUrl, getToken]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const createWallet = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/UserWallets`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw await parseApiError(response, `Failed to create wallet (${response.status})`);
      }

      await fetchWallets();
    } catch (createError) {
      console.error('Error creating wallet:', createError);
      setError(createError.message || 'Could not create wallet.');
      setShowErrorModal(true);
    }
  };

  const handleDeleteWallet = async (address) => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/UserWallets/${encodeURIComponent(address)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw await parseApiError(response, `Failed to delete wallet (${response.status})`);
      }

      setWallets((currentWallets) => currentWallets.filter((wallet) => wallet.walletAddress !== address));
    } catch (deleteError) {
      console.error('Error deleting wallet:', deleteError);
      setError(deleteError.message || 'Could not delete wallet.');
      setShowErrorModal(true);
    }
  };

  const toggleWallet = (index) => {
    setWallets((currentWallets) => currentWallets.map((wallet, walletIndex) => (
      walletIndex === index ? { ...wallet, collapsed: !wallet.collapsed } : wallet
    )));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch((copyError) => {
      console.error('Error copying address to clipboard:', copyError);
    });
  };

  const formatWalletAddress = (address) => String(address || '').trim();

  const handleSendBitcoin = async (formData) => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/UserWallets/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw await parseApiError(response, `Failed to send Bitcoin (${response.status})`);
      }

      const result = await response.json().catch(() => null);
      await fetchWallets();
      return result || true;
    } catch (sendError) {
      console.error('Error sending Bitcoin:', sendError);
      setError(sendError.message || 'Could not send Bitcoin.');
      setShowErrorModal(true);
      return false;
    }
  };

  return (
    <div className="category">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <h4 onClick={() => setCollapsed(!collapsed)} style={{ cursor: 'pointer' }}>
          BTC Wallets
        </h4>
        <button
          type="button"
          className="new-wallet-button"
          onClick={createWallet}
          title="Create New Wallet"
          aria-label="Create new BTC wallet"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {!collapsed && (
        <div>
          {wallets.length === 0 ? (
            <p>No BTC wallets found.</p>
          ) : (
            wallets.map((wallet, index) => {
              const walletAddress = formatWalletAddress(wallet.walletAddress);

              return (
              <div key={walletAddress || index} className="category wallet-card">
                <div className="wallet-summary-row">
                  <button
                    type="button"
                    className="expand-wallet-button"
                    onClick={() => toggleWallet(index)}
                    title={wallet.collapsed ? 'Show Details' : 'Hide Details'}
                    aria-label={wallet.collapsed ? 'Show wallet details' : 'Hide wallet details'}
                  >
                    <FontAwesomeIcon icon={faWallet} />
                  </button>
                  <BTCConverter
                    value={Number(wallet.confirmedBalance ?? wallet.balance ?? 0)}
                    fontType="Arial"
                    fontSize="16px"
                    height="30px"
                  />
                  <button
                    type="button"
                    className="send-funds-button"
                    onClick={() => {
                      setSelectedWalletAddress(walletAddress);
                      setShowModal(true);
                    }}
                    title="Send Funds"
                    aria-label="Send Bitcoin"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </div>

                {!wallet.collapsed && (
                  <div className="wallet-item">
                    <div className="wallet-info">
                      <div className="wallet-address-row">
                        <span className="wallet-address-label">Address:</span>
                        <code className="wallet-address" title={walletAddress}>{walletAddress}</code>
                      </div>
                      <button
                        type="button"
                        className="copy-wallet-button"
                        onClick={() => copyToClipboard(walletAddress)}
                        title="Copy"
                        aria-label="Copy wallet address"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      {Number(wallet.pendingBalance || 0) > 0 && (
                        <div className="wallet-pending-balance">
                          Pending:{' '}
                          <BTCConverter
                            value={Number(wallet.pendingBalance || 0)}
                            fontType="Arial"
                            fontSize="14px"
                            height="24px"
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="delete-wallet-button"
                      onClick={() => handleDeleteWallet(walletAddress)}
                      title="Delete wallet"
                      aria-label="Delete wallet"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>
      )}

      <p>
        Total Balance:{' '}
        <BTCConverter
          value={totalBalance}
          fontType="Arial"
          fontSize="16px"
          height="30px"
        />
      </p>
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} className="tyk-modal" centered>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Wallet action needs attention</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tyk-panel">
            <span className="tyk-kicker">BTC wallet</span>
            <p className="mb-0 text-slate-200">{error}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <SendBitcoinModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleSend={handleSendBitcoin}
        selectedWalletAddress={selectedWalletAddress}
      />
    </div>
  );
};

export default WalletList;
